import { addDays, format } from 'date-fns';
import { de } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';

import { Spacing, TextVariants } from '@lichtblick/theme';

import { CalendarAppointment } from './CalendarAppointment';
import {
  CalendarCellNewButton,
  CalendarDateHeaderLabel,
  DayAgendaContainer,
  DayAgendaSection,
  DayAgendaSectionTitle,
  EmptyDayMessage,
} from './InstallationCalendar.styles';
import { CalendarEvent } from './type';

import { Text } from '..';

type AgendaViewDailyProps = {
  endDate?: Date;
  events: CalendarEvent[];
  onCreateAppointment?: (date: Date) => void;
  onEventDrop?: (change: { end: Date; event: CalendarEvent; start: Date }) => void;
  onSelectEvent?: (event: CalendarEvent) => void;
  range?: Date[];
  startDate?: Date;
};

/**
 * Custom Agenda view that displays days vertically, with each day showing
 * appointments in the same visual style as the month view.
 * Designed for mobile/small viewports where full month view is cramped.
 */
export const AgendaViewDaily = ({
  endDate,
  events,
  onCreateAppointment,
  onEventDrop,
  onSelectEvent,
  startDate,
}: AgendaViewDailyProps) => {
  const [draggedEventData, setDraggedEventData] = useState<{ eventId: number; originalDate: Date } | null>(null);
  const [dragOverDayKey, setDragOverDayKey] = useState<string | null>(null);

  // Filter events to show only those in the visible date range
  const filteredEvents = useMemo(() => {
    if (!startDate || !endDate) {
      return events;
    }

    return events.filter((event) => {
      const eventDate = new Date(event.start);

      return eventDate >= startDate && eventDate <= endDate;
    });
  }, [events, startDate, endDate]);

  // Group events by day
  const eventsByDay = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {};

    filteredEvents.forEach((event) => {
      const dayKey = format(event.start, 'yyyy-MM-dd');

      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }

      grouped[dayKey].push(event);
    });

    return grouped;
  }, [filteredEvents]);

  // Generate list of days to display (next 30 days or based on range)
  const daysToDisplay = useMemo(() => {
    const start = startDate || new Date();
    const days = [];

    for (let i = 0; i < 30; i++) {
      days.push(addDays(start, i));
    }

    return days;
  }, [startDate]);

  const handleAppointmentDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    if (event.isBinding) {
      e.preventDefault();

      return;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ eventId: event.id, originalDate: event.start }));
    setDraggedEventData({ eventId: event.id, originalDate: event.start });
  };

  const handleDayDragOver = (e: React.DragEvent, dayKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDayKey(dayKey);
  };

  const handleDayDrop = (e: React.DragEvent, targetDay: Date) => {
    e.preventDefault();
    setDragOverDayKey(null);

    try {
      const data = e.dataTransfer.getData('application/json');
      const { eventId } = JSON.parse(data);

      const draggedEvent = events.find((ev) => ev.id === eventId);

      if (!draggedEvent || draggedEvent.isBinding) {
        return;
      }

      // Calculate the time offset between original start and end
      const offset = draggedEvent.end.getTime() - draggedEvent.start.getTime();
      const newStart = new Date(targetDay);

      newStart.setHours(
        draggedEvent.start.getHours(),
        draggedEvent.start.getMinutes(),
        draggedEvent.start.getSeconds(),
      );
      const newEnd = new Date(newStart.getTime() + offset);

      onEventDrop?.({ event: draggedEvent, start: newStart, end: newEnd });

      setDraggedEventData(null);
    } catch {
      // Handle error silently if drag data parsing fails
      setDraggedEventData(null);
    }
  };

  const handleDragLeave = () => {
    setDragOverDayKey(null);
  };

  return (
    <DayAgendaContainer>
      {daysToDisplay.map((day) => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const dayEvents = eventsByDay[dayKey] || [];

        return (
          <DayAgendaSection
            data-drag-over={dragOverDayKey === dayKey ? 'true' : 'false'}
            key={dayKey}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => handleDayDragOver(e, dayKey)}
            onDrop={(e) => handleDayDrop(e, day)}
          >
            <DayAgendaSectionTitle>
              <CalendarDateHeaderLabel $variant={TextVariants.body.m}>
                {format(day, 'EEEE, d MMMM yyyy', { locale: de })}
              </CalendarDateHeaderLabel>
            </DayAgendaSectionTitle>

            {dayEvents.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {dayEvents.map((event) => (
                  <div
                    draggable={!event.isBinding}
                    key={event.id}
                    onClick={() => onSelectEvent?.(event)}
                    onDragEnter={() => setDragOverDayKey(dayKey)}
                    onDragStart={(e) => handleAppointmentDragStart(e, event)}
                    style={{
                      cursor: event.isBinding ? 'default' : 'grab',
                      opacity: draggedEventData?.eventId === event.id ? 0.5 : 1,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <CalendarAppointment event={event} onEdit={onSelectEvent} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyDayMessage>
                <Text $variant={TextVariants.body.s} color="gray">
                  No appointments
                </Text>
              </EmptyDayMessage>
            )}
            <CalendarCellNewButton
              className="calendar-cell-new-btn"
              onClick={(e) => {
                e.stopPropagation();
                onCreateAppointment?.(day);
              }}
              style={{
                marginTop: Spacing.Xs,
              }}
              type="button"
            >
              + New Appointment
            </CalendarCellNewButton>
          </DayAgendaSection>
        );
      })}
    </DayAgendaContainer>
  );
};
